import * as React from "react";
import { encode } from "uqr";

import { cn } from "../cn";

// HARDEN-mfa: a QR code (an authenticator app's `otpauth://` enrolment link). The matrix comes
// from `uqr` (MIT, unjs; packages/ui/THIRD_PARTY.md); this draws it as one SVG path. Scanners
// want dark modules on a light ground with a quiet zone, so the tile stays light in both themes:
// the page's foreground on its background in light, and the reverse in dark.

export type QrCodeProps = Omit<React.ComponentProps<"svg">, "children"> & {
  /** What the code encodes. */
  value: string;
  /** What a screen reader hears (the image is the value; say what it is for). */
  label: string;
};

export function QrCode({ value, label, className, ...props }: QrCodeProps) {
  const { size, path } = React.useMemo(() => {
    // Medium error correction, a four-module quiet zone (ISO/IEC 18004).
    const qr = encode(value, { ecc: "M", border: 4 });
    let d = "";
    qr.data.forEach((row, y) => {
      row.forEach((dark, x) => {
        if (dark) d += `M${x} ${y}h1v1h-1z`;
      });
    });
    return { size: qr.size, path: d };
  }, [value]);
  return (
    <svg
      data-slot="qr-code"
      role="img"
      aria-label={label}
      viewBox={`0 0 ${size} ${size}`}
      shapeRendering="crispEdges"
      className={cn(
        "size-44 shrink-0 rounded-md border border-subtle bg-background fill-foreground dark:bg-foreground dark:fill-background",
        className,
      )}
      {...props}
    >
      <path d={path} />
    </svg>
  );
}
